import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import AddressFields from './AddressFields';
import { addressFieldProp } from './AddressFieldItem';

const addressProps = PropTypes.shape({
  address1: PropTypes.string,
  address2: PropTypes.string,
  city: PropTypes.string,
  country: PropTypes.string,
  district: PropTypes.string,
  firstName: PropTypes.string,
  isAPOFPO: PropTypes.bool,
  isBillingAllowed: PropTypes.bool,
  isDefaultShipping: PropTypes.bool,
  isShippingAllowed: PropTypes.bool,
  lastName: PropTypes.string,
  name: PropTypes.string,
  phoneticName: PropTypes.string,
  poBox: PropTypes.bool,
  postalCode: PropTypes.string,
  prefecture: PropTypes.string,
  province: PropTypes.string,
  state: PropTypes.string,
  title: PropTypes.string,
  zone: PropTypes.string,
});

const addressConfigProps = PropTypes.shape({
  id: PropTypes.string,
  code: PropTypes.string,
  key: PropTypes.string,
  name: PropTypes.string,
  region: PropTypes.string,
  storeLocator: PropTypes.bool,
  billingCountry: PropTypes.bool,
  shippingCountry: PropTypes.bool,
  fields: PropTypes.arrayOf(addressFieldProp),
  visibility: PropTypes.shape({
    firstName: PropTypes.bool,
    lastName: PropTypes.bool,
    country: PropTypes.bool,
    address1: PropTypes.bool,
    address2: PropTypes.bool,
    address3: PropTypes.bool,
    poBox: PropTypes.bool,
    city: PropTypes.bool,
    APOFPORegion: PropTypes.bool,
    state: PropTypes.bool,
    prefecture: PropTypes.bool,
    province: PropTypes.bool,
    postalCode: PropTypes.bool,
    region: PropTypes.bool,
    phone: PropTypes.bool,
    name: PropTypes.bool,
    phoneticName: PropTypes.bool,
    zone: PropTypes.bool,
    district: PropTypes.bool,
  }),
});

const propTypes = {
  countryId: PropTypes.string.isRequired,
  address: addressProps,
  type: PropTypes.oneOf(['shipping', 'billing']).isRequired,
  onChange: PropTypes.func.isRequired,
  addressConfig: addressConfigProps,
};

const defaultProps = {
  address: undefined,
  addressConfig: undefined,
};

export default function AddressForm(props) {
  const {
    address, addressConfig, countryId, type, onChange,
  } = props;
  const [addressFormData, setAddressFormData] = useState({});
  const nameFields = useMemo(() => ['firstName', 'lastName'], []);

  useEffect(() => {
    if (!addressConfig) {
      return;
    }

    const visibilityKeys = Object.keys(addressConfig.visibility);
    const visibleFields = visibilityKeys.filter((key) => !!addressConfig.visibility[key]);
    const formData = visibleFields.reduce((data, key) => ({
      ...data,
      [key]: address?.[key] ?? '',
    }), {});
    setAddressFormData(formData);
  }, [addressConfig, address]);

  const handleChange = useCallback((key, value) => {
    setAddressFormData({
      ...addressFormData,
      [key]: value,
    });
    onChange(key, value);
  }, [addressFormData, setAddressFormData, onChange]);

  const nameGroupFields = useMemo(
    () => addressConfig?.fields?.filter((field) => nameFields.includes(field.name)) || [],
    [addressConfig, nameFields],
  );

  const addressGroupFields = useMemo(
    () => addressConfig?.fields?.filter((field) => !nameFields.includes(field.name)) || [],
    [addressConfig, nameFields],
  );

  return (
    <section id={`form-${type}-${countryId}-${addressConfig.id}`} className={`${type}-info`}>
      <input id={`${type}-country`} name={`addresses.${type}.country`} type="hidden" value={countryId} />
      <fieldset className="field-group">
        <AddressFields
          fields={nameGroupFields}
          onChange={handleChange}
          addressFormData={addressFormData}
        />
      </fieldset>
      <fieldset className="field-group">
        <AddressFields
          fields={addressGroupFields}
          onChange={handleChange}
          addressFormData={addressFormData}
        />
      </fieldset>
    </section>
  );
}

AddressForm.propTypes = propTypes;
AddressForm.defaultProps = defaultProps;
